import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { BookmarkPlus, Files } from 'lucide-react';
import type { DocumentV3, TemplateDef } from '@docgen/schema';
import { BCE_TEMPLATES } from '@docgen/seed-bce';
import {
  Button,
  EmptyState,
  ErrorNotice,
  Modal,
  Pill,
  SearchInput,
  Skeleton,
  Tabs,
  useUrlSearch,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  PAGE_LIBRARY_SCOPE,
  PAGE_LIBRARY_VISIBILITY,
  useLibraryItem,
  useLibraryItems,
  type LibraryItemSummary,
} from '../lib/page-library';
import { previewDocument, templatePreviewPage } from '../lib/library-pages';
import { formatShortDate } from '../lib/money';
import { PageThumbnail, PagesPreview } from './PageThumbnail';

/** The picker's tabs: the caller's pages, the team's shared pages, and the built-in BCE templates. */
export const LIBRARY_PICKER_TAB = {
  mine: 'mine',
  team: 'team',
  templates: 'templates',
} as const;
export type LibraryPickerTab = (typeof LIBRARY_PICKER_TAB)[keyof typeof LIBRARY_PICKER_TAB];

const TAB_ITEMS = [
  { key: LIBRARY_PICKER_TAB.mine, label: 'My pages' },
  { key: LIBRARY_PICKER_TAB.team, label: 'Team' },
  { key: LIBRARY_PICKER_TAB.templates, label: 'BCE templates' },
] as const;

/** The picker's search lives in the URL under its own key, apart from any list on the page behind. */
export const LIBRARY_SEARCH_PARAM = 'libraryQ';

/** Card thumbnail width; the height follows the letter page. */
const THUMB_WIDTH_PX = 150;
/** Preview page width: readable without zooming, two pages tall in the dialog. */
const PREVIEW_WIDTH_PX = 600;

/** Who made a built-in template, as the cards say. */
const BCE_OWNER = 'Best Corporate Events';

export interface LibraryPickerProps {
  /** The proposal being edited: thumbnails render against its theme and data. */
  readonly doc: DocumentV3;
  /** Adds a library item after the selected page (saving the proposal first). */
  readonly onAddItem: (item: LibraryItemSummary) => Promise<void>;
  /** Adds a built-in template page after the selected page. */
  readonly onAddTemplate: (template: TemplateDef) => void;
  /** The empty state's first action: close and start picking pages to save. */
  readonly onStartSaving: () => void;
  readonly onClose: () => void;
}

/** What's open in the preview, if anything. */
type Previewing =
  | { readonly kind: 'item'; readonly item: LibraryItemSummary }
  | { readonly kind: 'template'; readonly template: TemplateDef }
  | null;

function lastUsedLabel(lastUsedAt: string | null): string {
  return lastUsedAt === null ? 'Not used yet' : `Last used ${formatShortDate(lastUsedAt)}`;
}

function pageCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'page' : 'pages'}`;
}

function matchesTemplate(template: TemplateDef, q: string): boolean {
  const needle = q.toLowerCase();
  return template.name.toLowerCase().includes(needle) || (template.description ?? '').toLowerCase().includes(needle);
}

/** The thumbnail of a library item's first page, which the list sends along. */
function ItemThumbnail({ item, doc }: { readonly item: LibraryItemSummary; readonly doc: DocumentV3 }): React.ReactElement {
  if (item.firstPage === null) {
    return <div aria-hidden className="h-[194px] w-[150px] rounded-[4px] border border-portal-line bg-white" />;
  }
  return <PageThumbnail doc={previewDocument(doc, [item.firstPage])} width={THUMB_WIDTH_PX} />;
}

interface CardProps {
  readonly name: string;
  readonly description: string;
  readonly thumbnail: React.ReactNode;
  readonly meta: readonly string[];
  readonly visibility?: LibraryItemSummary['visibility'];
  readonly busy: boolean;
  readonly onPreview: () => void;
  readonly onAdd: () => void;
}

function LibraryCard({ name, description, thumbnail, meta, visibility, busy, onPreview, onAdd }: CardProps): React.ReactElement {
  return (
    <li className="flex flex-col overflow-hidden rounded-portal-card border border-portal-line bg-white">
      <div className="flex justify-center bg-portal-mist px-3 py-4">{thumbnail}</div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-portal-small font-bold text-portal-ink">{name}</h3>
          {visibility === undefined ? null : (
            <Pill tone={visibility === PAGE_LIBRARY_VISIBILITY.team ? 'brand' : 'neutral'} className="shrink-0">
              {visibility === PAGE_LIBRARY_VISIBILITY.team ? 'Team' : 'Private'}
            </Pill>
          )}
        </div>
        {meta.map((line) => (
          <p key={line} className="text-portal-micro text-portal-muted">
            {line}
          </p>
        ))}
        {description ? <p className="line-clamp-2 text-portal-micro text-portal-muted">{description}</p> : null}
        <div className="mt-auto flex gap-2 pt-2">
          <Button variant="secondary" size="compact" onClick={onPreview} aria-label={`Preview ${name}`}>
            Preview
          </Button>
          <Button size="compact" onClick={onAdd} disabled={busy} aria-label={`Add ${name}`}>
            {busy ? 'Adding…' : 'Add'}
          </Button>
        </div>
      </div>
    </li>
  );
}

function ItemPreview({
  item,
  doc,
  busy,
  error,
  onAdd,
  onBack,
}: {
  readonly item: LibraryItemSummary;
  readonly doc: DocumentV3;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onAdd: () => void;
  readonly onBack: () => void;
}): React.ReactElement {
  const detail = useLibraryItem(item.id);
  return (
    <Modal
      open
      title={item.name}
      subtitle={[pageCountLabel(item.pageCount), item.ownerName].filter((part) => part !== '').join(' · ')}
      onClose={onBack}
      dismissible={!busy}
      className="max-w-3xl"
      phoneFullScreen
      actions={
        <>
          <Button variant="secondary" onClick={onBack} disabled={busy}>
            Back
          </Button>
          <Button onClick={onAdd} disabled={busy || !detail.data}>
            {busy ? 'Adding…' : 'Add'}
          </Button>
        </>
      }
    >
      {error ? <ErrorNotice inline title="Couldn't add these pages." message={error} /> : null}
      {item.description ? <p className="mb-3">{item.description}</p> : null}
      <div className="flex max-h-[65vh] justify-center overflow-y-auto rounded-portal-card bg-desk p-4">
        {detail.isError ? (
          <ErrorNotice inline message={`These pages couldn't be loaded. ${errorMessage(detail.error)}`} />
        ) : detail.data ? (
          <PagesPreview doc={previewDocument(doc, detail.data.pages, detail.data.dataFragment)} width={PREVIEW_WIDTH_PX} />
        ) : (
          <Skeleton className="h-[776px] w-[600px]" />
        )}
      </div>
    </Modal>
  );
}

/**
 * Add page → From library: pick saved pages (yours or the team's) or a
 * built-in BCE template, see them drawn with this proposal's data, and add
 * them after the selected page.
 */
export function LibraryPicker({ doc, onAddItem, onAddTemplate, onStartSaving, onClose }: LibraryPickerProps): React.ReactElement {
  const [tab, setTab] = useState<LibraryPickerTab>(LIBRARY_PICKER_TAB.mine);
  const [previewing, setPreviewing] = useState<Previewing>(null);
  const { search, setSearch, debounced } = useUrlSearch(LIBRARY_SEARCH_PARAM);

  const scope = tab === LIBRARY_PICKER_TAB.team ? PAGE_LIBRARY_SCOPE.team : PAGE_LIBRARY_SCOPE.mine;
  const items = useLibraryItems({ scope, q: debounced }, tab !== LIBRARY_PICKER_TAB.templates);
  const templates = useMemo(
    () => (debounced ? BCE_TEMPLATES.filter((template) => matchesTemplate(template, debounced)) : BCE_TEMPLATES),
    [debounced],
  );

  const add = useMutation({ mutationFn: (item: LibraryItemSummary) => onAddItem(item), onSuccess: () => close() });

  // The search belongs to this dialog: clear it from the URL on the way out.
  const close = (): void => {
    setSearch('');
    onClose();
  };

  const addTemplate = (template: TemplateDef): void => {
    onAddTemplate(template);
    close();
  };

  if (previewing?.kind === 'item') {
    return (
      <ItemPreview
        item={previewing.item}
        doc={doc}
        busy={add.isPending}
        error={add.isError ? errorMessage(add.error) : null}
        onAdd={() => add.mutate(previewing.item)}
        onBack={() => setPreviewing(null)}
      />
    );
  }
  if (previewing?.kind === 'template') {
    const { template } = previewing;
    return (
      <Modal
        open
        title={template.name}
        subtitle={`1 page · ${BCE_OWNER}`}
        onClose={() => setPreviewing(null)}
        className="max-w-3xl"
        phoneFullScreen
        actions={
          <>
            <Button variant="secondary" onClick={() => setPreviewing(null)}>
              Back
            </Button>
            <Button onClick={() => addTemplate(template)}>Add</Button>
          </>
        }
      >
        {template.description ? <p className="mb-3">{template.description}</p> : null}
        <div className="flex max-h-[65vh] justify-center overflow-y-auto rounded-portal-card bg-desk p-4">
          <PagesPreview doc={previewDocument(doc, [templatePreviewPage(template)])} width={PREVIEW_WIDTH_PX} />
        </div>
      </Modal>
    );
  }

  const emptySearch = debounced !== '';
  const clearSearch = (
    <Button variant="secondary" onClick={() => setSearch('')}>
      Clear search
    </Button>
  );

  let body: React.ReactNode;
  if (tab === LIBRARY_PICKER_TAB.templates) {
    body =
      templates.length === 0 ? (
        <EmptyState icon={<Files />} title={`No templates match "${debounced}"`} message="Try another word, or clear the search." action={clearSearch} />
      ) : (
        <ul aria-label="BCE templates" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((template) => (
            <LibraryCard
              key={template.id}
              name={template.name}
              description={template.description ?? ''}
              thumbnail={<PageThumbnail doc={previewDocument(doc, [templatePreviewPage(template)])} width={THUMB_WIDTH_PX} />}
              meta={[`1 page · ${BCE_OWNER}`]}
              busy={false}
              onPreview={() => setPreviewing({ kind: 'template', template })}
              onAdd={() => addTemplate(template)}
            />
          ))}
        </ul>
      );
  } else if (items.isPending) {
    body = (
      <ul aria-label="Loading pages" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((slot) => (
          <li key={slot}>
            <Skeleton className="h-[320px] w-full" />
          </li>
        ))}
      </ul>
    );
  } else if (items.isError) {
    body = <ErrorNotice inline title="Saved pages are unavailable." message={errorMessage(items.error)} />;
  } else if (items.data.items.length === 0) {
    body = emptySearch ? (
      <EmptyState icon={<Files />} title={`No pages match "${debounced}"`} message="Search looks through names, descriptions and the pages' own words." action={clearSearch} />
    ) : tab === LIBRARY_PICKER_TAB.mine ? (
      <EmptyState
        icon={<BookmarkPlus />}
        title="You haven't saved any pages yet"
        message="Pick pages in the Pages panel and save them to your library. They'll be here for every proposal you build."
        action={<Button onClick={onStartSaving}>Select pages to save</Button>}
      />
    ) : (
      <EmptyState
        icon={<Files />}
        title="No team pages yet"
        message="Pages someone shares with the whole team show up here. Share your own from My pages."
        action={
          <Button variant="secondary" onClick={() => setTab(LIBRARY_PICKER_TAB.mine)}>
            Go to My pages
          </Button>
        }
      />
    );
  } else {
    body = (
      <ul aria-label={tab === LIBRARY_PICKER_TAB.mine ? 'My pages' : 'Team pages'} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.data.items.map((item) => (
          <LibraryCard
            key={item.id}
            name={item.name}
            description={item.description}
            thumbnail={<ItemThumbnail item={item} doc={doc} />}
            meta={[
              [pageCountLabel(item.pageCount), item.ownerName].filter((part) => part !== '').join(' · '),
              lastUsedLabel(item.lastUsedAt),
            ]}
            visibility={item.visibility}
            busy={add.isPending && add.variables.id === item.id}
            onPreview={() => setPreviewing({ kind: 'item', item })}
            onAdd={() => add.mutate(item)}
          />
        ))}
      </ul>
    );
  }

  return (
    <Modal
      open
      title="Add pages from the library"
      subtitle="They go in after the selected page, filled in with this proposal's client, event and programs."
      onClose={close}
      dismissible={!add.isPending}
      className="max-w-5xl"
      phoneFullScreen
    >
      <Tabs label="Page library" items={TAB_ITEMS} active={tab} onChange={setTab} />
      <SearchInput
        label="Search pages"
        placeholder="Search names, descriptions and page text"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        className="mb-4"
        autoFocus
      />
      {add.isError ? <ErrorNotice inline title="Couldn't add these pages." message={errorMessage(add.error)} /> : null}
      <div className="max-h-[60vh] overflow-y-auto max-md:max-h-none">{body}</div>
    </Modal>
  );
}
